/**
 * Get cookie value by name
 */
function getCookie(name) {
    if (typeof document === 'undefined')
        return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
        return parts.pop()?.split(';').shift() || null;
    return null;
}
/**
 * ⚠️ IMPORTANT: Runtime Environment Detection
 *
 * This SDK uses RUNTIME hostname detection, NOT build-time environment variables.
 * This ensures the SDK always connects to the correct API based on where it's deployed.
 *
 * Environment detection priority (highest to lowest):
 * 1. **tagadaClientEnv** - Explicit override via URL param, localStorage, or cookie
 *    Example: ?tagadaClientEnv=production
 * 2. **Production domains** → production API (api.tagada.io)
 * 3. **Dev/staging domains** → development API (api.tagada.dev, vercel.app, etc.)
 * 4. **Localhost/local IPs** → local API (localhost, 127.0.0.1, etc.)
 *    - Can be overridden via window.__TAGADA_ENV__.TAGADA_ENVIRONMENT
 * 5. **Default fallback** → production API (safest)
 *
 * Build-time .env variables (VITE_*, REACT_APP_*, NEXT_PUBLIC_*) are IGNORED
 * to prevent incorrect API connections when plugins are deployed to different environments.
 */
/**
 * Environment configurations for different deployment environments
 */
export const ENVIRONMENT_CONFIGS = {
    production: {
        baseUrl: 'https://api.tagada.io',
        endpoints: {
            checkout: {
                sessionInit: '/api/v1/checkout/session/init',
                sessionInitAsync: '/api/v1/checkout/session/init-async',
                sessionStatus: '/api/v1/checkout/session/status',
                asyncStatus: '/api/public/v1/checkout/async-status',
            },
            customer: {
                profile: '/api/v1/customer/profile',
                session: '/api/v1/customer/session',
            },
            store: {
                config: '/api/v1/store/config',
            },
        },
    },
    development: {
        baseUrl: 'https://api.tagada.dev',
        endpoints: {
            checkout: {
                sessionInit: '/api/v1/checkout/session/init',
                sessionInitAsync: '/api/v1/checkout/session/init-async',
                sessionStatus: '/api/v1/checkout/session/status',
                asyncStatus: '/api/public/v1/checkout/async-status',
            },
            customer: {
                profile: '/api/v1/customer/profile',
                session: '/api/v1/customer/session',
            },
            store: {
                config: '/api/v1/store/config',
            },
        },
    },
    local: {
        baseUrl: 'http://app.localhost:3000',
        endpoints: {
            checkout: {
                sessionInit: '/api/v1/checkout/session/init',
                sessionInitAsync: '/api/v1/checkout/session/init-async',
                sessionStatus: '/api/v1/checkout/session/status',
                asyncStatus: '/api/public/v1/checkout/async-status',
            },
            customer: {
                profile: '/api/v1/customer/profile',
                session: '/api/v1/customer/session',
            },
            store: {
                config: '/api/v1/store/config',
            },
        },
    },
};
/**
 * Get the environment configuration based on the current environment
 *
 * Checks for custom base URL override via tagadaClientBaseUrl parameter
 */
export function getEnvironmentConfig(environment = 'local') {
    const apiConfig = ENVIRONMENT_CONFIGS[environment];
    if (!apiConfig) {
        console.warn(`Unknown environment: ${environment}. Falling back to local.`);
        return {
            environment: 'local',
            apiConfig: ENVIRONMENT_CONFIGS.local,
        };
    }
    // 🎯 Check for custom base URL override (URL > localStorage > cookie)
    let customBaseUrl = null;
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        customBaseUrl = urlParams.get('tagadaClientBaseUrl');
        if (!customBaseUrl) {
            try {
                customBaseUrl = localStorage.getItem('tgd_client_base_url') || getCookie('tgd_client_base_url');
            }
            catch {
                // Storage not available
            }
        }
    }
    // If custom base URL is set, override the apiConfig.baseUrl
    if (customBaseUrl) {
        console.log(`[SDK] Using custom API base URL override: ${customBaseUrl}`);
        return {
            environment,
            apiConfig: {
                ...apiConfig,
                baseUrl: customBaseUrl,
            },
        };
    }
    return {
        environment,
        apiConfig,
    };
}
/**
 * Build a complete API URL from environment config and endpoint path
 */
export function buildApiUrl(config, endpointPath) {
    return `${config.apiConfig.baseUrl}${endpointPath}`;
}
/**
 * Get a specific endpoint URL
 */
export function getEndpointUrl(config, category, endpoint) {
    const categoryEndpoints = config.apiConfig.endpoints[category];
    const endpointPath = categoryEndpoints[endpoint];
    if (!endpointPath) {
        throw new Error(`Endpoint not found: ${category}.${endpoint}`);
    }
    return buildApiUrl(config, endpointPath);
}
/**
 * Auto-detect environment based on hostname and URL patterns at RUNTIME
 * ⚠️ IMPORTANT: Ignores build-time .env variables to ensure correct detection in all environments
 * .env variables are ONLY used for local development via window.__TAGADA_ENV__
 *
 * Priority (highest to lowest):
 * 1. tagadaClientEnv - Explicit override via URL/localStorage/cookie
 * 2. __TAGADA_ENV__ - Local development override
 * 3. Hostname-based detection - Production/staging domains
 * 4. Default fallback - Production (safest)
 */
export function detectEnvironment() {
    console.log('[SDK] detectEnvironment() called');
    // Check if we're in browser
    if (typeof window === 'undefined') {
        return 'local'; // SSR fallback
    }
    // 🎯 PRIORITY 1: Check for explicit tagadaClientEnv override (URL > localStorage > cookie)
    // This allows forcing environment regardless of hostname
    const urlParams = new URLSearchParams(window.location.search);
    const urlEnv = urlParams.get('tagadaClientEnv');
    if (urlEnv && (urlEnv === 'production' || urlEnv === 'development' || urlEnv === 'local')) {
        console.log(`[SDK] Using explicit environment override: ${urlEnv}`);
        return urlEnv;
    }
    // Check localStorage/cookie for persisted override
    try {
        const storageEnv = localStorage.getItem('tgd_client_env') || getCookie('tgd_client_env');
        if (storageEnv && (storageEnv === 'production' || storageEnv === 'development' || storageEnv === 'local')) {
            console.log(`[SDK] Using persisted environment override: ${storageEnv}`);
            return storageEnv;
        }
    }
    catch {
        // Storage not available
    }
    const hostname = window.location.hostname;
    const href = window.location.href;
    console.log(`[SDK] detectEnvironment() - hostname: "${hostname}"`);
    // 1. Check for LOCAL environment first (highest priority for dev)
    // Local: localhost, local IPs, local domains, or ngrok tunnels (used for local dev)
    if (hostname === 'localhost' ||
        hostname.startsWith('127.') ||
        hostname.startsWith('192.168.') ||
        hostname.startsWith('10.') ||
        hostname.includes('.local') ||
        hostname === '' ||
        hostname === '0.0.0.0' ||
        hostname.includes('ngrok-free.dev') ||
        hostname.includes('ngrok-free.app') ||
        hostname.includes('ngrok.io') ||
        hostname.includes('ngrok.app') ||
        hostname.includes('.loclx.io')) {
        console.log('[SDK] detectEnvironment() - returning LOCAL');
        // For local development, allow override via window.__TAGADA_ENV__ (injected by dev server)
        if (typeof window !== 'undefined' && window?.__TAGADA_ENV__?.TAGADA_ENVIRONMENT) {
            const override = window.__TAGADA_ENV__.TAGADA_ENVIRONMENT.toLowerCase();
            if (override === 'production' || override === 'development' || override === 'local') {
                console.log(`[SDK] Local override detected: ${override}`);
                return override;
            }
        }
        return 'local';
    }
    // 2. Production: deployed to production domains
    if (hostname.includes('tagada.io') ||
        hostname.includes('tagadapay.com') ||
        hostname.includes('yourproductiondomain.com')) {
        return 'production';
    }
    // 3. Development: deployed to staging/dev domains or has dev indicators
    if (hostname.includes('tagada.dev') ||
        hostname.includes('tagadapay.dev') ||
        hostname.includes('vercel.app') ||
        hostname.includes('netlify.app') ||
        hostname.includes('surge.sh') ||
        hostname.includes('github.io') ||
        hostname.includes('herokuapp.com') ||
        hostname.includes('railway.app') ||
        href.includes('?env=dev') ||
        href.includes('?dev=true') ||
        href.includes('#dev')) {
        return 'development';
    }
    // 4. Default fallback for unknown domains (production is safest)
    console.warn(`[SDK] Unknown domain: ${hostname}, defaulting to production`);
    return 'production';
}
/**
 * Check if we're running in local development environment
 * Uses centralized detectEnvironment() for consistency
 *
 * @param excludeCdn - If true, excludes CDN subdomains (e.g., instance-id.cdn.localhost)
 * @returns true if in local environment (excluding CDN if specified)
 */
export function isLocalEnvironment(excludeCdn = false) {
    const env = detectEnvironment();
    if (env !== 'local') {
        return false;
    }
    // If we need to exclude CDN subdomains
    if (excludeCdn && typeof window !== 'undefined') {
        return !window.location.hostname.includes('.cdn.');
    }
    return true;
}
